import * as React from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import styled from 'styled-components';

import { NotificationCard } from '../NotificationCard/NotificationCard';
import { Group, GroupingType } from '../../utils/Grouping';
import { RemoveNotifications } from '../../../store/Actions';
import './NotificationGroup.scss';
import { StoredNotification } from '../../../model/StoredNotification';
import { GROUP_MINIMIZED_STACK_SIZE } from '../../../constants';
import { Icon } from '@openfin/ui-library';
import { OpenFrame } from '../../../store/partials/Frames';

interface Props extends ReturnType<typeof mapDispatch> {
  // Some extra information about group to handle progressive rendering
  group: Group;
  // A callback to expand/collapse group
  handleExpandCollapse: () => void;
  // A callback to check if group is expanded
  isExpanded: () => boolean;
}

function NotificationGroupComponent(props: Props) {
  const { group, openFrame, handleExpandCollapse, isExpanded, removeNotifications } = props;
  const headerTitleClassName = `single-line ${
    group.notifications.length > GROUP_MINIMIZED_STACK_SIZE ? 'multi-item' : 'single-item'
  }`;

  const handleClearAll = async () => {
    await removeNotifications(group.notifications);
  };

  const isStream = !!group.notifications[0].notification.stream;

  const handlePopoutFrame = () => {
    openFrame(group.key, isStream);
  };

  return (
    <div className="group">
      <div className="group-header">
        <div className="group-title">
          {group.type === GroupingType.APPLICATION && (
            <Icon onClick={handlePopoutFrame} icon="ExternalLinkIcon" className="popOutIcon" />
          )}
          <h2 className={headerTitleClassName}>{group.title}</h2>
          <div className="group-actions">
            {group.notifications.length > GROUP_MINIMIZED_STACK_SIZE && (
              <>
                <button className="action single-line expand-button" onClick={handleExpandCollapse}>
                  {isExpanded() ? 'Show Fewer' : `Show All (${group.notifications.length})`}
                </button>
                <div className="vertical-divider"></div>
              </>
            )}
            <button className="action single-line" onClick={handleClearAll}>
              Clear
            </button>
          </div>
        </div>
      </div>
      <CardListContainer exit={isExpanded()}>
        {[...group.notifications].slice(0, group.renderLimit).map((notification) => {
          return (
            <CSSTransition
              key={`${notification.id}${notification.notification.date}`}
              timeout={200}
              classNames="item"
            >
              <NotificationCard
                key={`${notification.id}${notification.notification.date}`}
                notification={notification}
                tabIndex={0}
              />
            </CSSTransition>
          );
        })}
      </CardListContainer>
    </div>
  );
}

const CardListContainer = styled(TransitionGroup)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.px.base};

  & .item-enter {
    opacity: 0;
  }

  & .item-exit {
    opacity: 0;
  }

  & .item-enter-done {
    opacity: 1;
  }

  & .item-enter-active {
    animation: fadeIn 200ms ease-in forwards;
  }

  & .item-exit-active {
    animation: fadeOutRight 200ms ease-in;
    perspective: 1000;
    pointer-events: none;
  }
`;

const mapDispatch = (dispatch: Dispatch) => ({
  removeNotifications: (notifications: StoredNotification[]) =>
    (dispatch(new RemoveNotifications(notifications)) as unknown) as Promise<void>,
  openFrame: (sender: string, stream: boolean) => dispatch(new OpenFrame(sender, stream)),
});

export const NotificationGroup = connect(undefined, mapDispatch)(NotificationGroupComponent);
